import {
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChange,
} from '@angular/core';
import { ExprForm } from '@mode-switch/express';
import { EncField, Encoding } from '@mode-vegas/vegas';
import {
  RenderDataModel,
  EncodingHelpers,
  computePalette,
  FieldFormatHelpers,
  BigValueHelpers,
} from '@mode/charts/util-vegas';
import { ChartTypes } from '@mode/shared/contract-common';
import { DEFAULT_CHART_FORMAT } from '@mode/shared/util-js';
import { isEqual } from 'lodash';
import { Subscription } from 'rxjs';

export enum RENDER_TYPE {
  designer = 'designer',
  report = 'report',
}

@Component({
  selector: 'mode-big-value-renderer',
  templateUrl: './big-value-renderer.component.html',
  styleUrls: ['./big-value-renderer.component.scss'],
})
export class BigValueRendererComponent implements OnInit, OnChanges, OnDestroy {
  @Input() token!: string;
  @Input() renderContext!: RENDER_TYPE;
  @Input() renderModel!: RenderDataModel;
  @Input() format!: ChartTypes.ChartFormat;
  @Input() fieldFormats!: ChartTypes.FieldFormatMap;
  @Input() encoding!: Encoding;
  @Input() maxFontSize!: Encoding;
  @Input() suspendRendering = false;

  @Output() renderStart = new EventEmitter();
  @Output() renderEnd = new EventEmitter<any>();

  formattedValue!: string;
  formattedChangeRate!: string;
  trendDirectionClass!: 'positive' | 'negative' | 'neutral';
  trendArrowStyle: Record<string, string | number> = {};
  trendColorCss!: string;

  hasIncompleteEncoding!: boolean;

  private subscription?: Subscription;

  constructor(private ngZone: NgZone) {}

  ngOnInit(): void {
    // default render context to report view
    this.renderContext = this.renderContext || RENDER_TYPE.report;
  }

  ngOnChanges(changes: { [propKey: string]: SimpleChange }) {
    const modelChanged = changes['renderModel'] != null;
    const encodingChanged =
      changes['encoding'] && !isEqual(changes['encoding'].currentValue, changes['encoding'].previousValue);
    const formatChanged =
      changes['format'] && !isEqual(changes['format'].currentValue, changes['format'].previousValue);
    const fieldFormatsChanged =
      changes['fieldFormats'] && !isEqual(changes['fieldFormats'].currentValue, changes['fieldFormats'].previousValue);

    if (
      !this.suspendRendering &&
      this.renderModel &&
      (modelChanged || encodingChanged || formatChanged || fieldFormatsChanged)
    ) {
      this.render();
    }
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  render() {
    this.hasIncompleteEncoding = false;
    this.renderStart.emit();

    if (this.encoding == null) {
      this.renderEnd.emit();
      return;
    }

    if (!EncodingHelpers.isChartEncodingComplete(this.encoding, 'bigValue')) {
      this.hasIncompleteEncoding = true;
      this.renderEnd.emit();
      return;
    }

    const chart: Highcharts.Chart = null as any;
    const chartFormat = this.format || DEFAULT_CHART_FORMAT;
    const palette = computePalette(chartFormat);

    try {
      const textField = this.encoding.marks.properties.text.values[0];
      const valueTextFormat = this.resolveFieldFormats(textField);
      const seriesData = this.renderModel.getSeriesData('*');
      const row = seriesData[seriesData.length - 1];
      const rawValue = row != null ? seriesData[seriesData.length - 1][(textField as any).id] : undefined;

      this.formattedValue = this.getFormattedValue(rawValue, valueTextFormat);

      const trendField = this.encoding.marks.properties.detail.values[0];
      if (trendField != null) {
        const trendTextFormat = this.resolveFieldFormats(trendField);
        this.calculateTrendValue(row, trendTextFormat, palette);
      }

      this.ngZone.run(() => this.renderEnd.emit());
    } catch (err) {
      this.renderEnd.emit({ $err: err });
      return;
    }

    return chart;
  }

  resolveFieldFormats(field: EncField) {
    const textFormat = BigValueHelpers.getPaneTextFormat(this.fieldFormats, field.formula);
    const compiled = this.renderModel.compileEncField(field);
    const resolvedTextFormat = FieldFormatHelpers.resolveTextFormatForCompiledField(compiled, textFormat);
    return resolvedTextFormat ?? textFormat;
  }

  getFormattedValue(rawValue: number | null, valueFormat: ChartTypes.TextFormat, form?: ExprForm): string {
    if (rawValue == null) {
      return '--';
    }

    let formatter = BigValueHelpers.getFormatterByType(valueFormat.type);

    if (valueFormat.type === ChartTypes.FormatType.Percent) {
      valueFormat = { ...valueFormat, ...{ percentTransform: true } };
    }

    switch (valueFormat.type) {
      case ChartTypes.FormatType.Financial:
      case ChartTypes.FormatType.Percent:
      case ChartTypes.FormatType.Number:
        return formatter(rawValue, valueFormat).toString();
      default:
        if (form === ExprForm.PctDifference) {
          formatter = BigValueHelpers.getFormatterByType(ChartTypes.FormatType.Percent);
          valueFormat.precision = 2;
          valueFormat.percentTransform = true;
          return formatter(rawValue, valueFormat).toString();
        } else {
          return formatter(rawValue, null).toString();
        }
    }
  }

  calculateTrendValue(row: any, trendFormat: ChartTypes.TextFormat, palette: ReadonlyArray<string>) {
    const trendField = this.renderModel.compileEncField(this.encoding.marks.properties.detail.values[0]);
    const rawValue = row != null ? row[(trendField as any).alias] : undefined;

    this.formattedChangeRate = this.getFormattedValue(
      rawValue != null ? Math.abs(rawValue) : rawValue,
      trendFormat,
      trendField.form
    );
    this.trendDirectionClass = rawValue === 0 || rawValue == null ? 'neutral' : rawValue > 0 ? 'positive' : 'negative';

    // Set Arrow color
    switch (this.trendDirectionClass) {
      case 'positive':
        this.trendColorCss = palette[2];
        this.trendArrowStyle = { 'border-bottom-color': this.trendColorCss };
        break;
      case 'negative':
        this.trendColorCss = palette[0];
        this.trendArrowStyle = { 'border-top-color': this.trendColorCss };
        break;
      case 'neutral':
        this.trendColorCss = palette[1];
        this.trendArrowStyle = { display: 'none' };
        break;
    }
  }

  get font() {
    return '"Graphik", Helvetica, Arial, sans-serif';
  }

  get hasTrend(): boolean {
    return this.encoding && this.encoding.marks.properties.detail.values[0] != null;
  }

  get trendLabel(): string {
    return this.format.bigValue.primaryTrendFormat.label as string;
  }
}
